<script setup>
import { ref, computed, shallowRef, nextTick, useSlots, watch } from 'vue';
import { __, __nx, __x } from 'JIX/gettext.js';
import JobsearchDropdown from './JobsearchDropdown.vue';
import { computeCumulativeSums } from 'JIX/jobsearch/utils.js';
import 'icons/service-jobsearch.svg';

const props = defineProps({
    inputClasses: {
        type: String,
        default: 'form-control form-control-lg',
    },
    inputIconSettings: {
        type: Object,
        default: () => ({}), // Example { size: 'sm', name: 'service-jobsearch' } parent should import the icon.
    },
});

const userInput = defineModel('userInput', { type: String, default: '' });

const showDropdown = defineModel('showDropdown', {
    type: Boolean,
    default: false,
});

const inputIcon = computed(() => ({
    size: 'sm',
    name: 'service-jobsearch',
    ...props.inputIconSettings,
}));

const emit = defineEmits(['suggestionSelected', 'queryParamRemoved', 'queryParamsRemoved', 'unhandledInput', 'emptyEnter', 'focusout']);

const container = ref();
const slots = useSlots();

function enterHandler(event, selectedIndex) {
    const input = userInput.value.trim();
    if (selectedIndex !== null) {
        event.preventDefault();
        handleClickHandler(selectedIndex);
    } else if (input) {
        emit('unhandledInput', event, { input });
    } else {
        emit('emptyEnter');
    }
    container.value?.resetSelectedIndex();
}

function handleFocusout() {
    const input = userInput.value.trim();
    if (input) {
        emit('focusout', { input });
    }
}

watch(() => container.value?.selectedIndex, selectedIndex => {
    const scrollContainer = container.value.$el.querySelector('.jobsearch-dropdown');
    if (selectedIndex === null) {
        scrollContainer.scrollTop = 0;
        return;
    }

    const items = scrollContainer.querySelectorAll('.jobsearch-dropdown-section__suggestion');
    const item = items[selectedIndex];
    if (item) {
        const offsetTop = item.offsetTop;
        const offsetBottom = offsetTop + item.offsetHeight;

        const scrollTop = scrollContainer.scrollTop;
        const scrollBottom = scrollTop + scrollContainer.offsetHeight;

        if (!(offsetTop >= scrollTop && offsetBottom <= scrollBottom)) {
            scrollContainer.scrollTop = offsetTop;
        }
    }
}, { flush: 'post' });

const showMainMenu = ref(true);
const submenuComponent = shallowRef();
const submenuComponentProps = shallowRef();
const submenuComponentEventListeners = shallowRef();

async function handleClickHandler(globalIndex) {
    await nextTick();
    const result = clickHandlers.value[globalIndex]();
    if (result.requestSubmenu) {
        showMainMenu.value = false;
        submenuComponent.value = result.component;
        submenuComponentProps.value = result.props;
        submenuComponentEventListeners.value = result.eventListeners;
    }
    if (result.emitEvent) {
        emit(result.emitEvent.name, result.emitEvent.params);
    }
}

async function handleClick(blockIndex, blockLocalIndex) {
    // When a block has no selectableItems, but may still make use of a click event. E.g. JobsearchRenderQuery.
    if (typeof blockLocalIndex !== 'number') {
        return;
    }

    await nextTick();
    const numPrevSelectableItems = Object.values(dropdownBlocksRef.value)
        .slice(0, blockIndex)
        .filter(block => block.show)
        .map(block => block.numSelectableItems)
        .reduce((a, b) => a + b, 0);

    const globalIndex = numPrevSelectableItems + blockLocalIndex;
    handleClickHandler(globalIndex);
}

function releaseSubmenu() {
    showMainMenu.value = true;
    submenuComponent.value = null;
    submenuComponentProps.value = null;
    submenuComponentEventListeners.value = null;
}

const dropdownBlocksRef = ref({});
function createDropdownBlocksRef(el, name) {
    // Note: the clickHandlers computed rely on the order in which the keys were inserted.
    dropdownBlocksRef.value[name] = el;
}


const clickHandlers = computed(() =>
    Object.values(dropdownBlocksRef.value)
        .filter(block => block.show)
        .flatMap(block => block.clickHandlers)
);

const numSelectableItems = computed(() =>
    Object.values(dropdownBlocksRef.value)
        .filter(block => block.show)
        .map(block => block.numSelectableItems)
        .reduce((a, b) => a + b, 0)
);

const hasContent = computed(() =>
    Object.values(dropdownBlocksRef.value)
        .filter(block => block.show)
        .some(block => block.hasContent)
);


const selectedIndexToLocalIndex = computed(() => {
    return (blockName, _blockIndex, selectedIndex) => {
        if (selectedIndex === null) {
            return null;
        }
        const visibleBlocks = Object.values(dropdownBlocksRef.value).filter(block => block.show);

        const block = visibleBlocks.findIndex(block => blockName === block.name);

        if (block === -1) {
            return null;
        }
        const numSelectableItemsPerVisibleBlock = visibleBlocks.map(block => block.numSelectableItems);

        const upperBoundsVisibleBlocks = computeCumulativeSums(numSelectableItemsPerVisibleBlock);
        let containingBlock = null;
        for (let index = 0; index < upperBoundsVisibleBlocks.length; index++) {
            if (selectedIndex < upperBoundsVisibleBlocks[index]) {
                containingBlock = index;
                break;
            }
        }
        if (block !== containingBlock) {
            return null;
        }
        return selectedIndex - numSelectableItemsPerVisibleBlock.slice(0, containingBlock).reduce((a, b) => a + b, 0);
    };
});

const focusInput = () => container.value?.inputField.focusInput();
const unFocusInput = () => container.value?.inputField.unFocusInput();

defineExpose({
    focusInput,
    unFocusInput,
});

</script>

<template>
    <JobsearchDropdown
        ref="container"
        v-model:show-dropdown="showDropdown"
        v-model:user-input="userInput"
        :dropdown-has-content="hasContent"
        :input-classes="inputClasses"
        :num-selectable-items="numSelectableItems"
        :input-icon="inputIcon"
        @enter="enterHandler"
        @focusout="handleFocusout"
        dropdown-classes="jobsearch-dropdown--geo"
    >
        <template #dropdown="{ selectedIndex }">
            <div v-show="showMainMenu">
                <template v-if="slots.default">
                    <Component
                        v-for="block, blockIndex in slots.default()"
                        :key="blockIndex"
                        :is="block"
                        :name="'block' + blockIndex"
                        :selected-index="selectedIndexToLocalIndex('block' + blockIndex, blockIndex, selectedIndex)"
                        @click="index => handleClick(blockIndex, index)"
                        :ref="el => createDropdownBlocksRef(el, 'block' + blockIndex)"
                        v-model:user-input="userInput"
                    />
                </template>
            </div>
            <!--TODO: Remove the subcomponent logic. We now only have 1 component-->
            <div class="jobsearch-dropdown-section jobsearch-dropdown-section--standalone " v-if="!showMainMenu">
                <Component
                    :is="submenuComponent"
                    v-bind="submenuComponentProps"
                    v-on="submenuComponentEventListeners"
                >
                    <button class="btn btn-secondary btn-sm" type="button" @click.prevent="releaseSubmenu">
                        {{ __('Tilbage') }}
                    </button>
                </Component>
            </div>
        </template>
    </JobsearchDropdown>
</template>
